/**
 * What the user is asking for, from the text alone. Used to pick a path that can actually deliver it
 * (an image generator, or agent mode with tools) instead of letting a plain chat model say "I can't".
 */
export type Intent = "image" | "agent" | null;

// Words that name a picture (English + Arabic spellings: ة/ه, with or without a leading ال).
const IMG_NOUN =
  /(?:\b(?:images?|pictures?|pics?|photos?|photograph|illustrations?|logos?|posters?|wallpapers?|artwork|portraits?|icons?|drawings?|paintings?|sketch(?:es)?|avatars?|thumbnails?)\b|(?:ال)?(?:صور[هة]?|صور|رسم[هة]?|لوجو|شعار|بوستر|ايقون[هة]|أيقون[هة]|تصميم|لوحة|افاتار|أفاتار))/i;
// Asking HOW / WHY / WHAT about images is a normal chat, not a request to make one.
const QUESTION =
  /(?:\?|؟|\b(?:how|why|what|which|where|when|can you explain)\b|^\s*(?:ازاي|إزاي|كيف|ليه|ليش|لماذا|ايه|إيه|ما |ماذا|هل |فين|امتى))/i;
// Imperatives/requests to produce one (Egyptian, Gulf, Levantine, MSA, English).
const IMG_VERB =
  /(?:\b(?:generate|create|make|draw|design|paint|render|produce|give me|show me|send me|i want|i need|can i get|get me)\b|(?:هات|هاتلي|هاتلى|اعمل|اعملي|اعملّي|اعملى|ولد|ولّد|انشئ|أنشئ|صمم|صمّم|ارسم|ارسملي|ارسمي|صورلي|صورني|اصنع|ابعت|ابعتلي|ورّيني|وريني|عايز|عاوز|عايزه|عاوزه|محتاج|محتاجه|اريد|أريد|ابغى|أبغى|بدي|ابي|خلي|ينفع|تقدر|تعمل|تعملي|تولد|تصمم))/i;

/** An image request: a picture-noun together with a request verb; or a short, non-question phrase like "صورة قطة". */
// "Draw …" is an image request on its own, whatever follows ("ارسم عربيه" = draw a car).
const DRAW = /(?:^|\s)(?:ارسم|ارسملي|ارسمي|ارسموا|draw|paint|sketch|illustrate)(?:\s|$)/i;

// Building software ("a website with a modern design") is not a picture request, even though "تصميم" means design.
const SOFTWARE =
  /(?:\b(?:website|web ?site|web ?page|web app|app|landing page|dashboard|ui|ux|html|css|frontend|front-end|react|api|script|code|software|saas|store|shop)\b|موقع|ويب|تطبيق|ابلكيشن|اپلكيشن|برنامج|صفحة هبوط|صفحه هبوط|لاندينج|داشبورد|لوحة تحكم|واجهة|نظام|منصة|متجر|كود|سكريبت)/i;

function isImageRequest(text: string): boolean {
  if (SOFTWARE.test(text) && !IMG_NOUN.test(text.replace(/(?:ال)?تصميم(?:ات)?/g, " ")))
    return false;
  if (DRAW.test(text) && !/^\s*(?:ازاي|إزاي|كيف|how|why|ليه)(?:\s|$)/i.test(text)) return true;
  if (!IMG_NOUN.test(text)) return false;
  if (/\b(image|picture|photo) of\b/i.test(text)) return true;
  const words = text.trim().split(/\s+/).length;
  if (QUESTION.test(text) && !IMG_VERB.test(text)) return false;
  if (IMG_VERB.test(text)) return !/^\s*(?:ازاي|إزاي|كيف|how)(?:\s|$)/i.test(text);
  return words <= 6;
}

const DOC_EN =
  "powerpoint|pptx|presentation|slide ?deck|slides|excel|xlsx|spreadsheet|word document|docx|workbook";
const DOC_AR =
  "بريزنتيشن|بريزينتيشن|بريزنتشن|عرض تقديمي|باوربوينت|شرائح|اكسل|إكسل|اكسيل|ملف ورد|مستند ورد|جدول بيانات";
// Only when the user asks for one to be MADE (a question *about* Excel stays a normal chat).
const AGENT = [
  new RegExp(
    `\\b(make|create|build|generate|write|prepare|draft|produce|export|convert|give me)\\b[^.\\n]{0,50}\\b(${DOC_EN})\\b`,
    "i",
  ),
  new RegExp(`\\b(${DOC_EN})\\b[^.\\n]{0,40}\\b(for me|about|on|with formulas)\\b`, "i"),
  new RegExp(
    `(?:اعمل|اعملي|اعملّي|سوي|انشئ|أنشئ|جهز|جهزلي|اكتب|اكتبلي|هات|هاتلي|عايز|عاوز|محتاج|ابني|صمم)[^.\\n]{0,40}(?:${DOC_AR})`,
  ),
];

// Schedules / rosters / budgets asked for as a table are files to build, not chat answers.
const SCHEDULE_TABLE =
  /(?:اعمل|اعملي|اعملّي|جهز|جهزلي|هات|هاتلي|عايز|عاوز|محتاج|ابني|صمم|سوي|make|create|build|generate)[^.\n]{0,30}(?:جدول|روستر|roster|schedule)[^.\n]{0,50}(?:شيفت|شفت|مناوب|روتيشن|موظف|رواتب|ميزاني|حضور|اجازات|إجازات|shift|rotation|staff|employee|payroll|budget|attendance)/i;
// An answer that offers a file ("do you want it as xlsx?") followed by a short "yes" must go to agent mode, where files can really be made.
const FILE_WORDS = new RegExp(`\\.?(?:xlsx|docx|pptx|pdf)\\b|${DOC_EN}|${DOC_AR}|ملف`, "i");
const YES =
  /^\s*(?:(?:نعم|ايوه|ايوة|أيوه|ايوا|اه|آه|أه|تمام|ماشي|اوكي|أوكي|اكيد|أكيد|طبعا|طبعاً|يلا|ياريت|موافق|ok|okay|yes|yeah|yep|sure|please|go ahead|do it)[\s!.,،؟?]*)+$/i;

/** True when `text` is a short "yes" and the assistant's last message offered to produce a file. */
export function confirmsFileOffer(text: string, lastAssistant: string): boolean {
  return YES.test(text) && FILE_WORDS.test(lastAssistant);
}

// Using a connected account (GitHub, Cloudflare, Supabase ...) needs agent mode: plain chat has no tools.
const CONNECTOR_WORD = /(?:\b(?:connectors?|mcp)\b|(?:ال)?كونكتور(?:ز|ات)?)/i;
const SERVICE =
  /\b(?:github|gitlab|cloudflare|supabase|slack|notion|linear|jira|sentry|stripe|vercel)\b/i;
const ACCOUNT_WORD =
  /(?:\b(?:repos?|repositories|issues?|pull requests?|prs?|commits?|workers?|d1|kv|r2|dns|database|tables?|projects?|my (?:account|accounts))\b|ريبو|حساب(?:ي|اتي)|قاعدة بيانات|جداول)/i;
const HOWTO = /^\s*(?:ازاي|إزاي|كيف|how (?:do|to|can)|what is|ايه هو|يعني ايه)(?:\s|$)/i;

function isConnectorRequest(text: string): boolean {
  if (HOWTO.test(text)) return false;
  return CONNECTOR_WORD.test(text) || (SERVICE.test(text) && ACCOUNT_WORD.test(text));
}

// Personal-assistant requests (contact someone for me, check my calendar) need the tools only agent mode has.
const PEOPLE_AR =
  /(?:^|\s)(?:اتصل|اتصلي|كلم|كلّم|كلمه|كلمها|رن|رنّ|راسل|بلغ|بلّغ|ابلغ)\s+(?:ب|ل|على)?\S+|(?:ابعت|ابعتله|ابعتلها|ابعتلي|بعت)\s+(?:له\s+|لها\s+)?(?:رسال[هة]|ايميل|إيميل|واتس(?:اب)?|مسج|sms)|(?:^|\s)قول(?:ه|ها|ي)?\s+ل\S+|(?:^|\s)قوله\s|(?:^|\s)قولها\s/;
const PEOPLE_EN =
  /\b(?:call|phone|ring|text|whatsapp|e-?mail|message|tell|ask|remind)\s+(?!me\b|us\b|you\b|it\b|this\b|that\b|the\b|a\b|an\b|my\b|our\b|them\b|him\b|her\b)\w+/i;
const CAL_AR =
  /(?:كالندر|الكالندر|التقويم|تقويمي|جدولي|مواعيدي|ميعادي|مشغول|فاضي|فاضى)|(?:ضيف|سجل|احجز|حط)\s+(?:ل?ي\s+)?(?:ميعاد|موعد|اجتماع|مقابل[هة])|(?:عندي|عندى)\s+(?:ميعاد|موعد|اجتماع|مقابل[هة])/;
const CAL_EN =
  /\b(?:my calendar|am i (?:free|busy)|are you free|when am i free|add (?:an? )?(?:meeting|event|appointment)|schedule (?:an? )?(?:meeting|call)|book (?:an? )?(?:meeting|slot)|free slots?)\b/i;
function isAssistantRequest(text: string): boolean {
  if (HOWTO.test(text)) return false;
  return PEOPLE_AR.test(text) || CAL_AR.test(text) || CAL_EN.test(text) || PEOPLE_EN.test(text);
}

export function detectIntent(text: string): Intent {
  if (isImageRequest(text)) return "image";
  if (
    AGENT.some((r) => r.test(text)) ||
    SCHEDULE_TABLE.test(text) ||
    isConnectorRequest(text) ||
    isAssistantRequest(text)
  )
    return "agent";
  return null;
}
